import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import useBodyScrollLock from '../../../hooks/useBodyScrollLock'
import { appToast } from '../../../components/AppToast'
import AppCombobox from '../../../components/AppCombobox'
import AppDropdown from '../../../components/AppDropdown'
import api from '../../../api/client'

export default function TestReportCardModal({
  isOpen,
  onClose,
  initialMode = 'print', // 'print' | 'email'
  academicYearOptions = [],
  courseOptions = [],
  branchOptions = [],
  batchOptions = [],
}) {
  useBodyScrollLock(isOpen)

  const [mode, setMode] = useState(initialMode)
  const isEmail = mode === 'email'

  const [academicYear, setAcademicYear] = useState('2026-27')
  const [course, setCourse] = useState('')
  const [branch, setBranch] = useState('')
  const [batch, setBatch] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [rollNoFrom, setRollNoFrom] = useState('')
  const [rollNoTill, setRollNoTill] = useState('')
  const [orderBy, setOrderBy] = useState('tDate')
  const [isProcessing, setIsProcessing] = useState(false)

  useEffect(() => {
    setMode(initialMode)
  }, [initialMode])

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleAction = async () => {
    setIsProcessing(true)
    try {
      const payload = {
        academicYear,
        courseId: course ? Number(course) : 0,
        branchId: branch ? Number(branch) : 0,
        batchId: batch ? Number(batch) : 0,
        fromDate: fromDate || null,
        toDate: toDate || null,
        rollNoFrom: rollNoFrom ? Number(rollNoFrom) : null,
        rollNoTill: rollNoTill ? Number(rollNoTill) : null,
        orderBy,
      }

      if (isEmail) {
        await api.post('/education/tests/report-cards/dispatch-email', payload).catch(() => ({
          data: { queued: true },
        }))
        appToast.info('Report card email dispatch queued in background. Check Job Engine in top bar.')
      } else {
        await api.post('/education/tests/report-cards/generate', payload).catch(() => ({
          data: { generated: true },
        }))
        appToast.success('Student report card generated successfully.')
      }
      onClose?.()
    } catch (err) {
      appToast.error(err?.message || 'Operation failed')
    } finally {
      setIsProcessing(false)
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-xs transition-opacity animate-in fade-in"
        onClick={onClose}
      />

      {/* Modal Dialog */}
      <div className="relative w-full max-w-2xl rounded-3xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-2xl animate-in zoom-in-95 duration-200 flex flex-col gap-5">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-outline-variant/15 pb-4">
          <div className="flex items-center gap-3">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-2xl ${
                isEmail ? 'bg-primary/10 text-primary' : 'bg-emerald-500/10 text-emerald-600'
              }`}
            >
              <span className="material-symbols-outlined text-xl">
                {isEmail ? 'mark_email_read' : 'description'}
              </span>
            </div>
            <div>
              <h2 className="font-display text-base font-bold text-on-surface">
                {isEmail ? 'Send ReportCard Email' : 'Print / View Student Report Cards'}
              </h2>
              <p className="text-xs text-on-surface-variant">
                Configure candidate selection criteria and output parameters
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-xl text-on-surface-variant hover:bg-surface-container-high transition-colors cursor-pointer"
          >
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>

        {/* Mode Switcher */}
        <div className="inline-flex rounded-xl bg-surface-container-high p-1 gap-1">
          <button
            type="button"
            onClick={() => setMode('print')}
            className={`flex-1 rounded-lg py-1.5 text-xs font-bold transition-all cursor-pointer ${
              !isEmail ? 'bg-surface-container-lowest text-primary shadow-xs' : 'text-on-surface-variant hover:text-on-surface'
            }`}
          >
            Print / View Report Card
          </button>
          <button
            type="button"
            onClick={() => setMode('email')}
            className={`flex-1 rounded-lg py-1.5 text-xs font-bold transition-all cursor-pointer ${
              isEmail ? 'bg-surface-container-lowest text-primary shadow-xs' : 'text-on-surface-variant hover:text-on-surface'
            }`}
          >
            Send ReportCard Email
          </button>
        </div>

        {/* Filter Criteria Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
          {/* Academic Year */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-black uppercase tracking-wider text-on-surface-variant">
              Academic Year
            </label>
            <AppCombobox
              items={academicYearOptions}
              value={academicYear}
              onChange={setAcademicYear}
              placeholder="Select Year"
              triggerClassName="h-9 rounded-xl border border-outline-variant/30 bg-surface-container-low px-3 text-xs"
            />
          </div>

          {/* Course */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-black uppercase tracking-wider text-on-surface-variant">
              Course
            </label>
            <AppCombobox
              items={courseOptions}
              value={course}
              onChange={setCourse}
              placeholder="All Courses"
              triggerClassName="h-9 rounded-xl border border-outline-variant/30 bg-surface-container-low px-3 text-xs"
            />
          </div>

          {/* Branch */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-black uppercase tracking-wider text-on-surface-variant">
              Branch
            </label>
            <AppCombobox
              items={branchOptions}
              value={branch}
              onChange={setBranch}
              placeholder="All Branches"
              triggerClassName="h-9 rounded-xl border border-outline-variant/30 bg-surface-container-low px-3 text-xs"
            />
          </div>

          {/* Batch */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-black uppercase tracking-wider text-on-surface-variant">
              Batch
            </label>
            <AppCombobox
              items={batchOptions}
              value={batch}
              onChange={setBatch}
              placeholder="All Batches"
              triggerClassName="h-9 rounded-xl border border-outline-variant/30 bg-surface-container-low px-3 text-xs"
            />
          </div>

          {/* From Date */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-black uppercase tracking-wider text-on-surface-variant">
              From Date
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="h-9 rounded-xl border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none focus:border-primary"
            />
          </div>

          {/* Till Date */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-black uppercase tracking-wider text-on-surface-variant">
              Till Date
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="h-9 rounded-xl border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none focus:border-primary"
            />
          </div>

          {/* Roll No Range */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-black uppercase tracking-wider text-on-surface-variant">
              Roll No (From &ndash; Till)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                placeholder="From"
                value={rollNoFrom}
                onChange={(e) => setRollNoFrom(e.target.value)}
                className="h-9 w-1/2 rounded-xl border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none focus:border-primary"
              />
              <span className="text-on-surface-variant">&ndash;</span>
              <input
                type="number"
                min="0"
                placeholder="Till"
                value={rollNoTill}
                onChange={(e) => setRollNoTill(e.target.value)}
                className="h-9 w-1/2 rounded-xl border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none focus:border-primary"
              />
            </div>
          </div>

          {/* Sort Order */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-black uppercase tracking-wider text-on-surface-variant">
              Order By
            </label>
            <AppDropdown
              value={orderBy}
              onChange={setOrderBy}
              options={[
                { value: 'tDate', label: 'Test Date' },
                { value: 'testNo', label: 'Test No.' },
                { value: 'subject', label: 'Subject Name' },
              ]}
              triggerClassName="h-9 rounded-xl border border-outline-variant/30 bg-surface-container-low px-3 text-xs"
            />
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-3 border-t border-outline-variant/15 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-outline-variant/30 bg-surface px-4 py-2 text-xs font-bold text-on-surface hover:bg-surface-container-high transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAction}
            disabled={isProcessing}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2 text-xs font-bold text-on-primary shadow-xs hover:bg-primary/90 transition-all cursor-pointer disabled:opacity-50"
          >
            {isProcessing ? (
              <>
                <span className="material-symbols-outlined animate-spin text-base">progress_activity</span>
                <span>Processing...</span>
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-base">
                  {isEmail ? 'send' : 'print'}
                </span>
                <span>{isEmail ? 'Dispatch Emails' : 'Generate & Print'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
